import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";

import Container from "components/__reusable/Container";
import YearButton from "./YearButton";
import LoadingDialog from "components/__reusable/LoadingDialog";
import useAppDispatch from "hooks/useAppDispatch";
import useAppSelector from "hooks/useAppSelector";
import { getClassesOfAcademicYear } from "features/class/classAction";
import { CLASS_PATH } from "router/pathName";
import { STUDENT } from "config/keywords";
import { setSortBy } from "features/class/classSlice";
import { ClassSortValue } from "config/constants";
import DeleteClassButton from "./DeleteClassButton";

const ClassTable = () => {
  const { t } = useTranslation();
  const { classes, viewedYear, sortBy, loading } = useAppSelector(
    (state) => state.class,
  );
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getClassesOfAcademicYear({ year: viewedYear }));
  }, [dispatch, viewedYear, sortBy]);

  useEffect(() => {
    return () => {
      dispatch(setSortBy({ sortBy: ClassSortValue.name_asc }));
    };
  }, [dispatch]);

  const changeSortOrder = () => {
    dispatch(
      setSortBy({
        sortBy:
          sortBy === ClassSortValue.name_asc
            ? ClassSortValue.name_dsc
            : ClassSortValue.name_asc,
      }),
    );
  };

  return (
    <div className="flex-1">
      <Container>
        <div className="h-full flex flex-col space-y-1">
          <div className="w-[99%] overflow-auto mb-3">
            <div className="flex whitespace-nowrap space-x-3">
              {<YearButton />}
            </div>
          </div>
          <div className="flex-1 overflow-auto relative">
            <div className="w-full h-full absolute">
              {loading && <LoadingDialog />}
              {!loading && classes.length === 0 && (
                <p>{t("_generic._no_data")}</p>
              )}
              {!loading && classes.length > 0 && (
                <table className="w-full lg:w-1/2 relative">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="w-[60%] p-2 bg-[#d8d8d8] border border-[#D9D9D9]">
                        <button
                          type="button"
                          className="w-full flex justify-center items-center space-x-2"
                          onClick={changeSortOrder}
                        >
                          <p>{t("_class._class_name")}</p>
                          <div>
                            {sortBy === ClassSortValue.name_asc && (
                              <TiArrowSortedUp />
                            )}
                            {sortBy === ClassSortValue.name_dsc && (
                              <TiArrowSortedDown />
                            )}
                          </div>
                        </button>
                      </th>
                      <th className="w-[40%] p-2 bg-[#d8d8d8] border border-[#D9D9D9]">
                        {t("_class._total_students")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((c) => (
                      <tr key={c.id}>
                        <td className="w-[60%] p-2 border border-[#D9D9D9]">
                          <Link
                            to={`${CLASS_PATH}/${c.id}/${STUDENT}`}
                            className="text-[#5B5B5B] underline transition-colors duration-200 ease-out hover:text-primary"
                          >
                            {`${c.year} - ${c.name}`}
                          </Link>
                        </td>
                        <td className="w-[40%] p-2 border border-[#D9D9D9] text-right">
                          {c.number_of_student ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <DeleteClassButton />
        </div>
      </Container>
    </div>
  );
};

export default ClassTable;
